import { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, List, ListItemButton, ListItemText, Chip, Skeleton, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { SearchResultGroup, SearchHit, RecentSearch } from '@/types/core/Search';

/**
 * Renders text with highlighted portions wrapped in <mark> tags.
 * Safely handles the highlight HTML from the backend.
 */
function HighlightedText({ text, highlight }: { text: string; highlight?: string }) {
  // If no highlight, just render plain text
  if (!highlight) {
    return <>{text}</>;
  }

  // Parse the highlight string which contains <mark>...</mark> tags
  // We split by <mark> and </mark> and render appropriately
  const parts = useMemo(() => {
    const result: { text: string; isHighlight: boolean }[] = [];
    let remaining = highlight;

    while (remaining.length > 0) {
      const markStart = remaining.indexOf('<mark>');

      if (markStart === -1) {
        // No more marks, add the rest as plain text
        if (remaining.length > 0) {
          result.push({ text: remaining, isHighlight: false });
        }
        break;
      }

      // Add text before the mark
      if (markStart > 0) {
        result.push({ text: remaining.slice(0, markStart), isHighlight: false });
      }

      // Find the closing mark
      const markEnd = remaining.indexOf('</mark>', markStart);
      if (markEnd === -1) {
        // Malformed, just add the rest
        result.push({ text: remaining.slice(markStart), isHighlight: false });
        break;
      }

      // Add the highlighted text
      const highlightedText = remaining.slice(markStart + 6, markEnd);
      result.push({ text: highlightedText, isHighlight: true });

      remaining = remaining.slice(markEnd + 7);
    }

    return result;
  }, [highlight]);

  return (
    <>
      {parts.map((part, i) =>
        part.isHighlight ? (
          <Box
            component="mark"
            key={i}
            sx={{
              bgcolor: 'warning.light',
              color: 'warning.contrastText',
              px: 0.25,
              borderRadius: 0.5,
            }}
          >
            {part.text}
          </Box>
        ) : (
          <span key={i}>{part.text}</span>
        )
      )}
    </>
  );
}

interface SearchResultsProps {
  groups: SearchResultGroup[];
  recentSearches?: RecentSearch[];
  selectedIndex: number;
  isLoading: boolean;
  query: string;
  onSelect: (hit: SearchHit) => void;
  onRecentSelect: (query: string) => void;
  /** Available shortcuts from metadata (e.g., { o: 'Order', c: 'Customer' }) */
  shortcuts?: Record<string, string>;
}

export function SearchResults({
  groups,
  recentSearches,
  selectedIndex,
  isLoading,
  query,
  onSelect,
  onRecentSelect,
  shortcuts,
}: SearchResultsProps) {
  const [selectedEntityType, setSelectedEntityType] = useState<string | null>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Build shortcuts hint from metadata (moved outside conditional for hooks rules)
  const shortcutHint = useMemo(() => {
    if (!shortcuts || Object.keys(shortcuts).length === 0) {
      return null;
    }
    const examples = Object.entries(shortcuts)
      .slice(0, 3)
      .map(([prefix, entity]) => `"${prefix}:" for ${entity.toLowerCase()}s`)
      .join(', ');
    return `Use shortcuts like ${examples}`;
  }, [shortcuts]);

  // Scroll selected item into view when navigating with keyboard
  useEffect(() => {
    if (resultsContainerRef.current) {
      const selectedElement = resultsContainerRef.current.querySelector(
        `[data-result-index="${selectedIndex}"]`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  // Show loading skeleton
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', height: '100%' }}>
        {/* Left column skeleton */}
        <Box sx={{ width: 180, borderRight: 1, borderColor: 'divider', p: 1 }}>
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 0.5, borderRadius: 1 }} />
          ))}
        </Box>
        {/* Right column skeleton */}
        <Box sx={{ flex: 1, p: 2 }}>
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 0.5, borderRadius: 1 }} />
          ))}
        </Box>
      </Box>
    );
  }

  // Show recent searches when query is empty
  if (!query && recentSearches && recentSearches.length > 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          Recent searches
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {recentSearches.map((recent, i) => (
            <Chip
              key={i}
              label={recent.query}
              size="small"
              onClick={() => onRecentSelect(recent.query)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  // No results
  if (query && groups.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No results found for "{query}"
        </Typography>
      </Box>
    );
  }

  // Empty state
  if (!query) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Type to search across all entities...
        </Typography>
        {shortcutHint && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {shortcutHint}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('slashCommand.hint', 'Type "/" for commands')}
        </Typography>
      </Box>
    );
  }

  // Filter groups based on selected entity type
  const filteredGroups = selectedEntityType
    ? groups.filter(g => g.entityType === selectedEntityType)
    : groups;

  // Calculate flat index for keyboard navigation
  let flatIndex = 0;

  // Calculate total count - if any group has more results than shown (-1 or > hits.length), show "+" indicator
  const hasMoreIndicator = groups.some(g => g.totalInEntity === -1 || g.totalInEntity > g.hits.length);
  const totalCount = hasMoreIndicator
    ? groups.reduce((acc, g) => acc + g.hits.length, 0)
    : groups.reduce((acc, g) => acc + g.totalInEntity, 0);

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left column - Entity type filters */}
      <Box
        sx={{
          width: 180,
          borderRight: 1,
          borderColor: 'divider',
          overflow: 'auto',
          flexShrink: 0,
        }}
      >
        {/* All results option */}
        <ListItemButton
          selected={selectedEntityType === null}
          onClick={() => setSelectedEntityType(null)}
          sx={{
            py: 1,
            px: 1.5,
            '&.Mui-selected': {
              bgcolor: 'action.selected',
              borderRight: 2,
              borderColor: 'primary.main',
            },
          }}
        >
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" fontWeight={selectedEntityType === null ? 600 : 400}>
                  All Results
                </Typography>
                <Chip
                  label={hasMoreIndicator ? `${totalCount}+` : totalCount}
                  size="small"
                  sx={{ height: 20, fontSize: '0.7rem', minWidth: 28 }}
                />
              </Box>
            }
          />
        </ListItemButton>

        <Divider sx={{ my: 0.5 }} />

        {/* Entity type filters */}
        {groups.map((group) => {
          const isSelected = selectedEntityType === group.entityType;

          return (
            <ListItemButton
              key={group.entityType}
              selected={isSelected}
              onClick={() => setSelectedEntityType(group.entityType)}
              sx={{
                py: 1,
                px: 1.5,
                '&.Mui-selected': {
                  bgcolor: 'action.selected',
                  borderRight: 2,
                  borderColor: 'primary.main',
                },
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" fontWeight={isSelected ? 600 : 400}>
                      {group.entityDisplayName}
                    </Typography>
                    <Chip
                      label={group.totalInEntity === -1 || group.totalInEntity > group.hits.length ? `${group.hits.length}+` : group.totalInEntity}
                      size="small"
                      color={isSelected ? 'primary' : 'default'}
                      sx={{ height: 20, fontSize: '0.7rem', minWidth: 28 }}
                    />
                  </Box>
                }
              />
            </ListItemButton>
          );
        })}
      </Box>

      {/* Right column - Results list */}
      <Box ref={resultsContainerRef} sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {filteredGroups.map((group) => {
          return (
            <Box key={group.entityType} sx={{ mb: 2 }}>
              {/* Group header - only show if not filtering by entity type */}
              {!selectedEntityType && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    {group.entityDisplayName}
                  </Typography>
                </Box>
              )}

              {/* Group items */}
              <List dense disablePadding>
                {group.hits.map((hit) => {
                  const currentIndex = flatIndex;
                  const isSelected = currentIndex === selectedIndex;
                  flatIndex++;

                  return (
                    <ListItemButton
                      key={hit.id}
                      data-result-index={currentIndex}
                      selected={isSelected}
                      onClick={() => onSelect(hit)}
                      sx={{
                        borderRadius: 1,
                        mx: 0.5,
                        '&.Mui-selected': {
                          bgcolor: 'action.selected',
                          '&:hover': {
                            bgcolor: 'action.selected',
                          },
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={500} component="span">
                              <HighlightedText text={hit.primary} highlight={hit.highlight} />
                            </Typography>
                            {hit.tertiary && (
                              <Chip
                                label={hit.tertiary}
                                size="small"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {hit.secondary && (
                              <Typography variant="caption" color="text.secondary">
                                {hit.secondary}
                              </Typography>
                            )}
                            {hit.meta && (
                              <Typography variant="caption" color="text.disabled">
                                {hit.meta}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
